import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PageLayout from '../layout/PageLayout';
import ProjectOverview from '../pages/ProjectOverview/ProjectOverview';
import RealmOverview from '../pages/RealmOverview/RealmOverview';
import RealmView from '../pages/RealmView/RealmView';
import NewProjectView from '../pages/ProjectView/NewProjectView';
import ProjectView from '../pages/ProjectView/ProjectView';
import NewRealmView from '../pages/RealmView/NewRealmView';
import CortexDecoderKeysOverview from '../pages/CortexDecoderKeysOverview/CortexDecoderKeysOverview';
import ViewCortexDecoderKeyView from '../pages/CortexDecoderKeyView/ViewCortexDecoderKeyView';
import EditCortexDecoderKeyView from '../pages/CortexDecoderKeyView/EditCortexDecoderKeyView';
import CreateCortexDecoderKeyView from '../pages/CortexDecoderKeyView/CreateCortexDecoderKeyView';
import AppsOverview from '../pages/AppsOverview/AppsOverview';
import ViewAppView from '../pages/AppView/ViewAppView';
import EditAppView from '../pages/AppView/EditAppView';
import CreateAppView from '../pages/AppView/CreateAppView';
import OrganizationsOverview from '../pages/OrganizationsOverview/OrganizationsOverview';
import CreateOrganizationView from '../pages/OrganizationView/CreateOrganizationView';
import ViewOrganizationView from '../pages/OrganizationView/ViewOrganizationView';
import EditOrganizationView from '../pages/OrganizationView/EditOrganizationView';
import CheckoutView from '../pages/CheckoutView/CheckoutView';
import GeneralForm from '../pages/CheckoutView/GeneralForm';
import PaymentConfigurationForm from '../pages/CheckoutView/PaymentConfigurationForm';
import VposForm from '../pages/CheckoutView/VposForm';
import TseConfigurationForm from '../pages/CheckoutView/TseConfigurationForm';
import TaxCategoriesForm from '../pages/CheckoutView/TaxCategoriesForm';
import DiscountReasonsForm from '../pages/CheckoutView/DiscountReasonsForm';
import FulfillmentConfigForm from '../pages/CheckoutView/FulfillmentConfigForm';
import FlowForm from '../pages/CheckoutView/FlowForm';
import ControlConfigForm from '../pages/CheckoutView/ControlConfigForm';
import CheckConfigForm from '../pages/CheckoutView/CheckConfigForm';
import CustomerPropertiesLookupForm from '../pages/CheckoutView/CustomerPropertiesLookupForm';
import CustomerValidationForm from '../pages/CheckoutView/CustomerValidationForm';
import VoucherProvidersForm from '../pages/CheckoutView/VoucherProvidersForm';
import DepositReturnVoucherProvidersForm from '../pages/CheckoutView/DepositReturnVoucherProvidersForm';
import ServiceCredentialsForm from '../pages/CheckoutView/ServiceCredentialsForm';
import CouponConfigurationForm from '../pages/CheckoutView/CouponConfigurationForm';
import SetsForm from '../pages/CheckoutView/SetsForm';
import ReturnsForm from '../pages/CheckoutView/ReturnsForm';
import OverrideTemplatesForm from '../pages/CheckoutView/OverrideTemplatesForm';
import CashMovementForm from '../pages/CheckoutView/CashForm';
import TransactionsView from '../pages/TransactionView/TransactionsView';
import ReportingView from '../pages/TransactionView/ReportingView';
import CleanupScheduleView from '../pages/TransactionView/CleanupScheduleView';
import ClosingLogSinkView from '../pages/TransactionView/ClosingLogSinkView';
import CheckoutDevicesView from '../pages/CheckoutDevicesView/CheckoutDevicesView';
import DynaConfigView from '../pages/CheckoutDevicesView/DynaConfigView';
import SidebarView from '../pages/SidebarView/SidebarView';
import ExportView from '../pages/ExportView/ExportView';
import ThirdPartyExportView from '../pages/ExportView/ThirdPartyExportView';
import TransactionsStatisticsView from '../pages/ExportView/TransactionsStatisticsView';
import DynaFormPreview from '../tools/DynaFormPreview/DynaFormPreview';
import { login } from '../utils/login/loginUtils';
import AppRoutes from './routes';

function AppWithLayout({ children }) {
  const rawToken = useSelector((state) => state.token.value.raw);
  const authorized = useSelector((state) => state.authorization.authorized);

  if (!rawToken || !authorized) {
    login();

    return <PageLayout />;
  }

  return <PageLayout>{children}</PageLayout>;
}

export default function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route
          path={AppRoutes.Login}
          element={<Navigate to={AppRoutes.Root} replace />}
        />
        <Route
          path={AppRoutes.Root}
          element={
            <AppWithLayout>
              <ProjectOverview />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.ProjectsNew}
          element={
            <AppWithLayout>
              <NewProjectView />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.ProjectsClone(':projectId')}
          element={
            <AppWithLayout>
              <NewProjectView />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.ProjectsViewWildcard}
          element={
            <AppWithLayout>
              <Navigate to="project" replace />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.Project(':projectId')}
          element={
            <AppWithLayout>
              <ProjectView />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.Checkout(':projectId')}
          element={
            <AppWithLayout>
              <Navigate to="general" replace />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CheckoutGeneral(':projectId')}
          element={
            <AppWithLayout>
              <CheckoutView FormView={GeneralForm} key="checkoutView-general" />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CheckoutPaymentConfiguration(':projectId')}
          element={
            <AppWithLayout>
              <CheckoutView
                FormView={PaymentConfigurationForm}
                key="checkoutView-paymentConfiguration"
              />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CheckoutFlows(':projectId')}
          element={
            <AppWithLayout>
              <CheckoutView FormView={FlowForm} key="checkoutView-flows" />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CheckoutVpos(':projectId')}
          element={
            <AppWithLayout>
              <CheckoutView FormView={VposForm} key="checkoutView-vpos" />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CheckoutControlConfig(':projectId')}
          element={
            <AppWithLayout>
              <CheckoutView
                FormView={ControlConfigForm}
                key="checkoutView-controlConfig"
              />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CheckoutTseConfiguration(':projectId')}
          element={
            <AppWithLayout>
              <CheckoutView
                FormView={TseConfigurationForm}
                key="checkoutView-tseConfiguration"
              />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CheckoutDiscountReasons(':projectId')}
          element={
            <AppWithLayout>
              <CheckoutView
                FormView={DiscountReasonsForm}
                key="checkoutView-discountReasons"
              />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CheckoutTaxCategories(':projectId')}
          element={
            <AppWithLayout>
              <CheckoutView
                FormView={TaxCategoriesForm}
                key="checkoutView-taxCategories"
              />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CheckoutCheckConfig(':projectId')}
          element={
            <AppWithLayout>
              <CheckoutView
                FormView={CheckConfigForm}
                key="checkoutView-checkConfig"
              />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CheckoutCustomerPropertiesLookup(':projectId')}
          element={
            <AppWithLayout>
              <CheckoutView
                FormView={CustomerPropertiesLookupForm}
                key="checkoutView-customerPropertiesLookup"
              />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CheckoutFulfillmentConfig(':projectId')}
          element={
            <AppWithLayout>
              <CheckoutView
                FormView={FulfillmentConfigForm}
                key="checkoutView-fulfillmentConfig"
              />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CheckoutCustomerValidation(':projectId')}
          element={
            <AppWithLayout>
              <CheckoutView
                FormView={CustomerValidationForm}
                key="checkoutView-customerValidation"
              />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CheckoutVoucherProviders(':projectId')}
          element={
            <AppWithLayout>
              <CheckoutView
                FormView={VoucherProvidersForm}
                key="checkoutView-voucherProviders"
              />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CheckoutDepositReturnVoucherProviders(':projectId')}
          element={
            <AppWithLayout>
              <CheckoutView
                FormView={DepositReturnVoucherProvidersForm}
                key="checkoutView-depositReturnVoucherProviders"
              />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CheckoutServiceCredentials(':projectId')}
          element={
            <AppWithLayout>
              <CheckoutView
                FormView={ServiceCredentialsForm}
                key="checkoutView-serviceCredentials"
              />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CheckoutCouponConfiguration(':projectId')}
          element={
            <AppWithLayout>
              <CheckoutView
                FormView={CouponConfigurationForm}
                key="checkoutView-couponConfiguration"
              />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CheckoutSets(':projectId')}
          element={
            <AppWithLayout>
              <CheckoutView FormView={SetsForm} key="checkoutView-sets" />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CheckoutReturn(':projectId')}
          element={
            <AppWithLayout>
              <CheckoutView FormView={ReturnsForm} key="checkoutView-return" />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CheckoutOverrideTemplates(':projectId')}
          element={
            <AppWithLayout>
              <CheckoutView
                FormView={OverrideTemplatesForm}
                key="checkoutView-overrideTemplates"
              />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CheckoutCash(':projectId')}
          element={
            <AppWithLayout>
              <CheckoutView
                FormView={CashMovementForm}
                key="checkoutView-cash"
              />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.Transaction(':projectId')}
          element={
            <AppWithLayout>
              <Navigate to="transactions" replace />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.TransactionTransactions(':projectId')}
          element={
            <AppWithLayout>
              <TransactionsView />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.TransactionReporting(':projectId')}
          element={
            <AppWithLayout>
              <ReportingView />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.TransactionCleanupSchedule(':projectId')}
          element={
            <AppWithLayout>
              <CleanupScheduleView />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.TransactionClosingLogSink(':projectId')}
          element={
            <AppWithLayout>
              <ClosingLogSinkView />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CheckoutDevices(':projectId')}
          element={
            <AppWithLayout>
              <Navigate to="available-types" replace />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CheckoutDevicesAvailableTypes(':projectId')}
          element={
            <AppWithLayout>
              <CheckoutDevicesView />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CheckoutDevicesDynaConfig(':projectId')}
          element={
            <AppWithLayout>
              <DynaConfigView />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.Sidebar(':projectId')}
          element={
            <AppWithLayout>
              <SidebarView />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.Export(':projectId')}
          element={
            <AppWithLayout>
              <ExportView />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.ExportThirdParty(':projectId')}
          element={
            <AppWithLayout>
              <ThirdPartyExportView />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.ExportTransactionsStatistics(':projectId')}
          element={
            <AppWithLayout>
              <TransactionsStatisticsView />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.Realms}
          element={
            <AppWithLayout>
              <RealmOverview />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.RealmsNew}
          element={
            <AppWithLayout>
              <NewRealmView />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.RealmsClone(':realmID')}
          element={
            <AppWithLayout>
              <NewRealmView />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.RealmsView(':realmID')}
          element={
            <AppWithLayout>
              <RealmView />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CortexDecoderKeys}
          element={
            <AppWithLayout>
              <CortexDecoderKeysOverview />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CortexDecoderKeysNew}
          element={
            <AppWithLayout>
              <CreateCortexDecoderKeyView />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CortexDecoderKeysView(':keyID')}
          element={
            <AppWithLayout>
              <ViewCortexDecoderKeyView />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.CortexDecoderKeysEdit(':keyID')}
          element={
            <AppWithLayout>
              <EditCortexDecoderKeyView />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.Apps}
          element={
            <AppWithLayout>
              <AppsOverview />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.AppsNew}
          element={
            <AppWithLayout>
              <CreateAppView />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.AppsView(':appID')}
          element={
            <AppWithLayout>
              <ViewAppView />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.AppsEdit(':appID')}
          element={
            <AppWithLayout>
              <EditAppView />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.Organizations}
          element={
            <AppWithLayout>
              <OrganizationsOverview />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.OrganizationsNew}
          element={
            <AppWithLayout>
              <CreateOrganizationView />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.OrganizationsView(':organizationID')}
          element={
            <AppWithLayout>
              <ViewOrganizationView />
            </AppWithLayout>
          }
        />
        <Route
          path={AppRoutes.OrganizationsEdit(':organizationID')}
          element={
            <AppWithLayout>
              <EditOrganizationView />
            </AppWithLayout>
          }
        />
        <Route
          path="/tools"
          element={<Navigate to={AppRoutes.ToolsDynaformPreview} replace />}
        />
        <Route
          path={AppRoutes.ToolsDynaformPreview}
          element={
            <AppWithLayout>
              <DynaFormPreview />
            </AppWithLayout>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
